import { CryptoProxy } from '@protontech/crypto';
import { c } from 'ttag';

import { getFileExtension, isImage, isRAWExtension, isRAWPhoto, isVideo } from '@proton/shared/lib/helpers/mimetype';
import { traceError } from '@proton/shared/lib/helpers/sentry';
import { getItem } from '@proton/shared/lib/helpers/storage';

import { NodeWithSameNameExistsValidationError } from '../../../index';
import { type ExtendedAttributesMetadata, generatePhotosExtendedAttributes } from '../../extendedAttributes';
import { generateThumbnail } from '../../thumbnails';
import { UploadDriveClientRegistry } from '../UploadDriveClientRegistry';
import { useUploadControllerStore } from '../store/uploadController.store';
import type { PhotosUploadTask } from '../types';
import { createFileStream } from '../utils/createFileStream';
import { uploadLogError } from '../utils/uploadLogger';
import { TaskExecutor } from './TaskExecutor';

/**
 * Executes file uploads and emits events
 * NO store access - only emits events
 */
// TODO: Improve abort check and update thumbnail generator to support abortController
export class PhotosUploadExecutor extends TaskExecutor<PhotosUploadTask> {
    async execute(task: PhotosUploadTask): Promise<void> {
        const controllerStore = useUploadControllerStore.getState();
        const storedController = controllerStore.getController(task.uploadId);

        if (!storedController) {
            return;
        }

        const abortController = storedController.abortController;

        if (abortController.signal.aborted) {
            return;
        }

        if (task.file.size === 0 && !task.allowEmptyFile) {
            void this.eventCallback?.({
                type: 'file:empty',
                uploadId: task.uploadId,
                isForPhotos: true,
            });
            return;
        }

        try {
            const drivePhotos = UploadDriveClientRegistry.getDrivePhotosClient();

            let duplicateUids: string[] = [];
            try {
                duplicateUids = await drivePhotos.findPhotoDuplicates(
                    task.file.name,
                    async () => {
                        const fileStream = createFileStream(task.file);
                        const hashResult = await CryptoProxy.computeHashStream({
                            algorithm: 'unsafeSHA1',
                            dataStream: fileStream,
                        });
                        return hashResult.toHex();
                    },
                    abortController.signal
                );
            } catch (error) {
                if (abortController.signal.aborted) {
                    return;
                }
                uploadLogError('Photo duplicate detection failed', error, {
                    uploadId: task.uploadId,
                });
            }

            if (duplicateUids.length > 0) {
                void this.eventCallback?.({
                    type: 'photo:exist',
                    uploadId: task.uploadId,
                    duplicateUids,
                });
                return;
            }

            if (abortController.signal.aborted) {
                return;
            }

            const { thumbnails, mediaInfo, mimeType } = await this.generateThumbnails(task.file);

            if (abortController.signal.aborted) {
                return;
            }

            const extension = getFileExtension(task.file.name);
            const isSupportedPhotoType =
                isImage(mimeType) || isVideo(mimeType) || isRAWPhoto(mimeType) || isRAWExtension(extension);

            if (!isSupportedPhotoType) {
                void this.eventCallback?.({
                    type: 'photo:unsupported',
                    uploadId: task.uploadId,
                });
                return;
            }

            const metadata = await this.createFileUploaderMetadata(
                task.file,
                mimeType,
                mediaInfo,
                task.isUnfinishedUpload
            );

            if (abortController.signal.aborted) {
                return;
            }

            await this.eventCallback?.({
                type: 'file:prepared',
                uploadId: task.uploadId,
                isForPhotos: true,
            });

            if (abortController.signal.aborted) {
                return;
            }

            const uploader = await drivePhotos.getFileUploader(task.file.name, metadata, abortController.signal);

            const stream = createFileStream(task.file);
            const controller = await uploader.uploadFromStream(stream, thumbnails, (uploadedBytes: number) => {
                void this.eventCallback?.({
                    type: 'file:progress',
                    uploadId: task.uploadId,
                    uploadedBytes,
                    isForPhotos: true,
                });
            });

            void this.eventCallback?.({
                type: 'file:started',
                uploadId: task.uploadId,
                controller,
                isForPhotos: true,
            });

            const { nodeUid } = await controller.completion();

            const photosRoot = await drivePhotos.getMyPhotosRootFolder();

            void this.eventCallback?.({
                type: 'file:complete',
                uploadId: task.uploadId,
                nodeUid,
                parentUid: photosRoot.uid,
                isUpdatedNode: false,
                isForPhotos: true,
                isEmpty: task.allowEmptyFile,
            });
        } catch (error) {
            if (abortController.signal.aborted) {
                return;
            }

            if (error instanceof NodeWithSameNameExistsValidationError) {
                void this.eventCallback?.({
                    type: 'file:conflict',
                    uploadId: task.uploadId,
                    error,
                    isForPhotos: true,
                });
            } else {
                void this.eventCallback?.({
                    type: 'file:error',
                    uploadId: task.uploadId,
                    error: error instanceof Error ? error : new Error(c('Error').t`Upload failed`),
                    isForPhotos: true,
                });
            }
        }
    }

    private async generateThumbnails(file: File) {
        const { thumbnailsPromise, mimeTypePromise } = generateThumbnail(file, file.name, file.size, {
            debug: Boolean(getItem('proton-drive-debug', 'false')),
        });

        const [thumbnailsResult, mimeType] = await Promise.all([thumbnailsPromise, mimeTypePromise]);

        const result = thumbnailsResult.ok ? thumbnailsResult.result : undefined;
        const mediaInfo = result
            ? {
                  width: result.width,
                  height: result.height,
                  duration: result.duration,
              }
            : undefined;

        return {
            thumbnails: result?.thumbnails || [],
            mediaInfo,
            mimeType,
        };
    }

    private async createFileUploaderMetadata(
        file: File,
        mimeType: string,
        mediaInfo?: { width?: number; height?: number; duration?: number },
        isUnfinishedUpload?: boolean
    ): Promise<{
        mediaType: string;
        expectedSize: number;
        modificationTime: Date;
        overrideExistingDraftByOtherClient?: boolean;
        additionalMetadata: ExtendedAttributesMetadata | undefined;
        tags: (0 | 3 | 1 | 2 | 7 | 4 | 5 | 6 | 8 | 9)[];
        mainPhotoLinkID: string | undefined;
        captureTime: Date | undefined;
    }> {
        try {
            const { metadata, tags, captureTime } = await generatePhotosExtendedAttributes(file, mimeType, mediaInfo);
            return {
                mediaType: mimeType,
                expectedSize: file.size,
                mainPhotoLinkID: undefined,
                captureTime,
                tags: tags as (0 | 3 | 1 | 2 | 7 | 4 | 5 | 6 | 8 | 9)[],
                modificationTime: new Date(file.lastModified),
                overrideExistingDraftByOtherClient: isUnfinishedUpload,
                additionalMetadata: metadata,
            };
        } catch (error) {
            uploadLogError('Failed to generate extended attributes', error);
            traceError(error, {
                level: 'debug', // Debug as we need it only when we investigate issues.
                tags: {
                    component: 'generateExtendedAttributes',
                },
            });
            return {
                mediaType: mimeType,
                expectedSize: file.size,
                mainPhotoLinkID: undefined,
                captureTime: undefined,
                tags: [],
                modificationTime: new Date(file.lastModified),
                overrideExistingDraftByOtherClient: isUnfinishedUpload,
                additionalMetadata: undefined,
            };
        }
    }
}
